import Box, { BoxProps } from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { ReactNode } from 'react';

import {
  ticketCardActionsSx,
  ticketCardAvatarShellSx,
  ticketCardAvatarTopRowSx,
  ticketCardAvatarWrapperSx,
  ticketCardBadgeSx,
  ticketCardBadgesRowSx,
  ticketCardFooterSx,
  ticketCardInnerSx,
  ticketCardMediaSx,
  ticketCardOuterSx,
  ticketCardStatusBorderSx,
} from 'src/common/styles/ticketCardStyles';

interface TicketCardBadge {
  label: string;
  highlighted?: boolean;
  accent?: 'clay' | 'blue' | 'neutral';
  onClick?: () => void;
  tooltip?: ReactNode;
}

interface TicketCardProps extends Omit<BoxProps, 'title'> {
  eyebrow?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  badges?: TicketCardBadge[];
  avatar?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  media?: ReactNode;
  inverted?: boolean;
  selected?: boolean;
  statusStripColor?: string;
  innerSx?: SxProps<Theme>;
  /** When true with avatar, title renders in the top row beside the avatar instead of below. */
  titleBesideAvatar?: boolean;
  /** Opaque panel for OG image capture (no backdrop-filter). */
  solidPanel?: boolean;
}

export default function TicketCard({
  eyebrow,
  title,
  description,
  badges,
  avatar,
  actions,
  footer,
  media,
  inverted = false,
  selected = false,
  statusStripColor,
  titleBesideAvatar = false,
  solidPanel = false,
  children,
  sx,
  innerSx,
  ...boxProps
}: TicketCardProps) {
  const theme = useTheme();
  const hasAvatar = Boolean(avatar);
  const hasTitle = title != null && title !== '';
  const styleOptions = { inverted, selected, hasAvatar, solidPanel };
  const showTitleBelowAvatar = hasTitle && (!titleBesideAvatar || !hasAvatar);
  const titleTypographySx = {
    color: inverted ? 'primary.contrastText' : 'text.primary',
  } as const;

  const card = (
    <Box
      {...boxProps}
      sx={[
        ticketCardOuterSx(theme, styleOptions),
        ...(statusStripColor ? [ticketCardStatusBorderSx(statusStripColor)] : []),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ] as SxProps<Theme>}
    >
      {actions ? <Box sx={ticketCardActionsSx(inverted)}>{actions}</Box> : null}
      {media ? <Box sx={ticketCardMediaSx}>{media}</Box> : null}
      <Box sx={[ticketCardInnerSx(theme, styleOptions), ...(innerSx ? [innerSx] : [])] as SxProps<Theme>}>
        {hasAvatar ? (
          <>
            <Box sx={ticketCardAvatarTopRowSx}>
              {titleBesideAvatar && hasTitle ? (
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    gridColumn: 2,
                    ...titleTypographySx,
                  }}
                >
                  {title}
                </Typography>
              ) : eyebrow ? (
                <Typography
                  variant="label"
                  component="div"
                  sx={{
                    gridColumn: 2,
                    color: inverted ? 'primary.contrastText' : 'text.secondary',
                    opacity: inverted ? 0.85 : 1,
                  }}
                >
                  {eyebrow}
                </Typography>
              ) : null}
            </Box>
            {showTitleBelowAvatar ? (
              <Typography
                variant="h5"
                component="div"
                sx={{
                  mb: description ? 0.75 : 0,
                  ...titleTypographySx,
                }}
              >
                {title}
              </Typography>
            ) : null}
          </>
        ) : (
          <>
            {eyebrow ? (
              <Typography
                variant="label"
                component="div"
                sx={{
                  mb: 0.75,
                  color: inverted ? 'primary.contrastText' : 'text.secondary',
                  opacity: inverted ? 0.85 : 1,
                }}
              >
                {eyebrow}
              </Typography>
            ) : null}
            {hasTitle ? (
              <Typography
                variant="h5"
                component="div"
                sx={{
                  mb: description ? 0.75 : 0,
                  color: inverted ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {title}
              </Typography>
            ) : null}
          </>
        )}
        {hasTitle && description ? (
          <Typography
            variant="caption"
            component="div"
            sx={{
              color: inverted ? 'primary.contrastText' : 'text.secondary',
              opacity: inverted ? 0.9 : 1,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Typography>
        ) : null}
        {children}
        {badges?.length ? (
          <Box sx={ticketCardBadgesRowSx}>
            {badges.map(badge => {
              const chip = (
                <Chip
                  label={badge.label}
                  size="small"
                  clickable={Boolean(badge.onClick)}
                  onClick={badge.onClick}
                  sx={ticketCardBadgeSx(
                    theme,
                    Boolean(badge.highlighted),
                    inverted,
                    badge.accent,
                  )}
                />
              );
              if (!badge.tooltip) {
                return <span key={badge.label}>{chip}</span>;
              }
              return (
                <Tooltip key={badge.label} title={badge.tooltip}>
                  <span>{chip}</span>
                </Tooltip>
              );
            })}
          </Box>
        ) : null}
      </Box>
      {footer ? <Box sx={ticketCardFooterSx(inverted)}>{footer}</Box> : null}
    </Box>
  );

  if (!hasAvatar) {
    return card;
  }

  return (
    <Box sx={ticketCardAvatarShellSx}>
      <Box sx={ticketCardAvatarWrapperSx(theme, inverted, solidPanel)}>{avatar}</Box>
      {card}
    </Box>
  );
}
